import delay from 'delay';
import { delegate } from '../utils/tools';

import * as preact from 'preact';
import { useState } from 'preact/hooks';
import * as bodyLock from '../utils/dom/body-lock';
import * as wpConfig from '../config/wp-settings';
import * as globalOptions from '../config/options';
import globalState from '../config/state';

const el = {};

const state = {
	open: false,
	rendered: false,
};

const options = {
	translateBodyOff: '-275px',
	translateMenuOff: '275px',
};

export function Icon({ name }) {
	return <i className={`icon icon-${name}`} />;
}

function MobileSidebarGlobalNav({ menuItems }) {
	return (
		<div className="mobile-menu__global-nav">
			<ul className="mobile-menu__global-nav-list">
				{menuItems.map(item => (
					<NavItemLevel1 item={item} />
				))}
			</ul>
		</div>
	);
}

export function prefixUrl(url) {
	return url.startsWith('/') ? `https://wp.stolaf.edu${url}` : url;
}

export function linkOrLabel(item) {
	if (item.label.replace(/&nbsp;/g, ' ').trim() === '') {
		return null;
	}

	let html = { __html: item.label };

	if (item.url === '#') {
		return <span dangerouslySetInnerHTML={html} />;
	} else {
		return <a href={prefixUrl(item.url)} dangerouslySetInnerHTML={html} />;
	}
}

function NavItemLevel1({ item }) {
	let expandedState = useState(false);
	let expanded = expandedState[0];
	let setExpanded = expandedState[1];

	let hasChildren = item.menu_items.length > 0;

	return (
		<li className="mobile-menu__global-nav-item">
			<span className="mobile-menu__global-nav-highlight">
				<a
					className="mobile-menu__global-nav-link"
					href={prefixUrl(item.url)}
					data-js="mobile-global-toggle"
					onClick={event => {
						// allow the second click to visit the link
						if (hasChildren && !expanded) {
							event.preventDefault();
							setExpanded(true);
						}
					}}
					dangerouslySetInnerHTML={{ __html: item.label }}
				/>
			</span>

			{hasChildren && expanded ? (
				<div
					className="mobile-menu__global-subnav"
					data-js="mobile-global-subnav"
				>
					{item.menu_items.map(secondLevel => (
						<NavItemLevel2 item={secondLevel} />
					))}
				</div>
			) : null}
		</li>
	);
}

function NavItemLevel2({ item }) {
	let label = linkOrLabel(item);

	return (
		<>
			{label ? (
				<h5 className="mobile-global__dropdown-subheading">{label}</h5>
			) : null}

			{item.menu_items.length ? (
				<ul className="mobile-global__dropdown-list">
					{item.menu_items.map(thirdLevel => (
						<NavItemLevel3 item={thirdLevel} />
					))}
				</ul>
			) : null}
		</>
	);
}

function NavItemLevel3({ item }) {
	return (
		<li className="mobile-global__dropdown-list-item">
			<a
				className="mobile-global__dropdown-list-link"
				href={prefixUrl(item.url)}
			>
				<span
					className="mobile-global__dropdown-list-text"
					dangerouslySetInnerHTML={{ __html: item.label }}
				/>
			</a>
		</li>
	);
}

function MobileSidebarResourcesNav({ menuItems }) {
	return (
		<div className="mobile-menu__resources-nav">
			<h3 className="mobile-menu__resources-nav-title">Resources for</h3>
			<ul className="mobile-menu__resources-nav-list">
				{menuItems.map(item => {
					return (
						<li className="mobile-menu__resources-nav-item">
							<a
								className="mobile-menu__resources-nav-link"
								href={prefixUrl(item.url)}
								dangerouslySetInnerHTML={{ __html: item.label }}
							/>
						</li>
					);
				})}

				<li className="mobile-menu__resources-nav-item">
					<a
						className="mobile-menu__resources-nav-link"
						href="https://wp.stolaf.edu/giving/"
					>
						Supporting St. Olaf
					</a>
				</li>
			</ul>
		</div>
	);
}

function MobileSidebar({ data }) {
	return (
		<nav
			id="mobile-menu"
			className="mobile-menu"
			aria-labelledby="mobilemenu"
			data-js="mobile-menu"
			aria-hidden="true"
		>
			<h2 id="mobilemenu" className="u-visual-hide">
				Site Navigation
			</h2>

			<form
				className="mobile-nav__search"
				method="GET"
				action="https://www.stolaf.edu/search.cfm"
			>
				<div className="mobile-nav__search-wrap">
					<Icon name="search" />
					<label
						htmlFor="mobile-search"
						className="u-screen-reader-text"
					>
						Search sitewide:
					</label>
					<input
						name="q"
						type="search"
						id="mobile-search"
						className="site__search-input"
						placeholder="search sitewide"
					/>
				</div>
			</form>

			{data.global_nav.menu_items.length ? (
				<MobileSidebarGlobalNav
					menuItems={data.global_nav.menu_items}
				/>
			) : null}

			{data.resources_nav.menu_items.length ? (
				<MobileSidebarResourcesNav
					menuItems={data.resources_nav.menu_items}
				/>
			) : null}

			<div className="mobile-menu__resources-nav">
				<h3 className="mobile-menu__tools-nav-title">Tools</h3>
				<ul className="mobile-menu__tools-nav-list">
					<li className="mobile-menu__tools-nav-item">
						<a
							className="mobile-menu__tools-nav-link"
							href="https://www.stolaf.edu/directory/search/"
						>
							<Icon name="directory" />
							Directory
						</a>
					</li>
					<li className="mobile-menu__tools-nav-item">
						<a
							className="mobile-menu__tools-nav-link"
							href="https://wp.stolaf.edu/calendar/"
						>
							<Icon name="calendar" />
							Calendar
						</a>
					</li>
					<li className="mobile-menu__tools-nav-item">
						<a
							className="mobile-menu__tools-nav-link"
							href="https://wp.stolaf.edu/a-z/"
						>
							<Icon name="a-z" />
							A-Z
						</a>
					</li>
					<li className="mobile-menu__tools-nav-item">
						<a
							className="mobile-menu__tools-nav-link"
							href="https://www.stolaf.edu/multimedia/"
						>
							<Icon name="stream" />
							Streams
						</a>
					</li>
				</ul>
			</div>
		</nav>
	);
}

/**
 * @function
 * @description
 */

const renderMenu = () => {
	let container = document.createElement('div');
	el.topNav.parentNode.appendChild(container);
	preact.render(
		<MobileSidebar data={wpConfig.OFF_CANVAS_ITEMS} />,
		container
	);

	el.mobileMenu = document.querySelector('[data-js=mobile-menu]');

	state.rendered = true;
};

/**
 * @function
 * @description
 */

const showMenu = () => {
	bodyLock.lock();
	el.toggle.classList.add('menu-trigger--active');
	el.mobileMenu.style.transform = 'translateX(0)';
	el.topNav.style.transform = `translateX(${options.translateBodyOff})`;
	el.content.style.transform = `translateX(${options.translateBodyOff})`;
	if (el.alert) {
		el.alert.style.transform = `translateX(${options.translateBodyOff})`;
	}
	if (el.sidebars) {
		el.sidebars.forEach(item => item.setAttribute('disabled', 'disabled'));
	}
	if (el.categories) {
		el.categories.setAttribute('disabled', 'disabled');
	}
	state.open = true;
};

/**
 * @function
 * @description
 */

const hideMenu = () => {
	bodyLock.unlock();
	el.toggle.classList.remove('menu-trigger--active');
	el.mobileMenu.style.transform = `translateX(${options.translateMenuOff})`;
	el.content.style.transform = '';
	el.topNav.style.transform = '';
	if (el.footer) {
		el.footer.style.transform = '';
	}
	if (el.alert) {
		el.alert.style.transform = '';
	}
	if (el.sidebars) {
		el.sidebars.forEach(item => item.removeAttribute('disabled'));
	}
	if (el.categories) {
		el.categories.removeAttribute('disabled');
	}
	state.open = false;
};

/**
 * @function toggleMobileMenu
 * @description Toggle the mobile menu into and out of view
 */

const toggleMobileMenu = e => {
	e.stopPropagation();
	if (!state.rendered) {
		renderMenu();
		delay(50).then(showMenu);
		return;
	}

	if (state.open) {
		hideMenu();
		return;
	}
	showMenu();
};

/**
 * @function handleResize
 * @description
 */

const handleResize = () => {
	if (globalState.v_width >= globalOptions.NAV_BREAKPOINT && state.open) {
		hideMenu();
	}
};

/**
 * @function bindEvents
 * @description Bind event handlers
 */

const bindEvents = () => {
	delegate(
		el.topNav,
		'[data-js="trigger-mobile-menu"]',
		'click',
		toggleMobileMenu
	);

	document.addEventListener('modern_tribe/resize_executed', handleResize);
};

/**
 * @function init
 * @description Kick off this modules functionality
 */

const init = () => {
	el.topNav = document.querySelector('[data-js=top-nav]');

	if (!el.topNav) {
		return;
	}

	el.content = document.querySelector('[data-js=site-wrap]');
	el.toggle = document.querySelector('[data-js=trigger-mobile-menu]');
	el.footer = document.querySelector('[data-js=secondary-footer-nav]');
	el.alert = document.querySelector('[data-js=sto-alert]');
	el.sidebars = document.querySelector('[data-js=sidebar-section-tog]');
	el.categories = document.querySelector(
		'[data-js=blog-category-nav-toggle]'
	);

	bindEvents();

	console.info('Initialized mobile menu script scripts');
};

export default init;
